import * as gsf from "core/src/core/game-state-functions.js";
import * as cf from "core/src/core/core-functions.js";
import * as trajectoryUtil from "core/src/core/geometry/trajectory/trajectory-util.js";
import * as shapeSpatialRelations from "core/src/core/geometry/shape-spatial-relations.js";
import * as ShapeToGridConverter from "core/src/core/geometry/shape-to-grid-converter.js";
import { PLAY_AREA_FREE, IMMUNITY_DISTANCE_MULTIPLIER } from "core/src/core/constants.js";
import * as respawn from "core/src/core/respawn.js";

const converter = ShapeToGridConverter.createShapeToGridConverter();
const SUB = 0.012; // opponent trail paint sampling step (game-time seconds, ~1px)

// Client-side collision for the LOCAL player's own worm, so a crash shows the
// instant it happens on your screen instead of after the server's death round-trip.
// The server still confirms the death (authoritative).
//
// We mirror the engine's grid collision into a local grid:
//  - your OWN trail: painted from your short segments at each start (you own them),
//  - OPPONENT trails: painted by sampling their (merged/growing) streamed segments
//    over time, which is robust to how they arrive,
//  - WALL: a geometric map-bounds check.
// `getOwnTrackId` returns the trackId of the ONE worm this collision instance guards.
// (Was keyed by playerId + first-matching track; a player can now own more than one
// worm - a twin - so each worm's collision must guard its OWN track and treat the
// other same-player worm as an opponent trail it can crash into.)
export function createLocalCollision(getOwnTrackId: () => string | null) {
  let ownCursor = 0; // own segments painted so far
  let oppPaintedTo: Record<string, number> = {}; // per opponent track, game-time painted to
  let ownDist = 0;
  let ownDistFromCells: Record<number, number> = {};
  let ghostCursors: Record<string, number> = {}; // per wall-hack ghost track, segments painted
  let dead = false;
  // What killed us, as decided HERE (null while alive): the death report's "local"
  // half (death-report.ts), the same facts the [death:local] console line prints.
  let crash: any = null;
  // This worm's OWN private collision grid. Crucially NOT shared via the
  // gameState's playArea: with two local worms (two-on-one-device) a shared grid
  // would have each worm paint over the other's trail + immunity bookkeeping and
  // they'd kill each other the instant they moved. Each instance binds its own
  // grid onto gameState.playArea.grid before painting/checking (rendering is
  // synchronous, one worm fully resolved before the next).
  let grid: any[] | null = null;

  function reset() {
    ownCursor = 0;
    oppPaintedTo = {};
    ownDist = 0;
    ownDistFromCells = {};
    ghostCursors = {};
    dead = false;
    crash = null;
    grid = null;
  }

  function ownTrack(_gameState: any): string | null {
    return getOwnTrackId();
  }

  // Make this instance's private grid the active one for core's grid helpers.
  // Returns false until the play area dimensions are known.
  function bind(gameState: any): boolean {
    const pa = gameState.playArea;
    if (!pa || !pa.rows || !pa.cols) return false;
    if (!grid) grid = new Array(pa.rows * pa.cols).fill(PLAY_AREA_FREE);
    pa.grid = grid;
    return true;
  }

  // Paint own + opponent trails into the grid. Call every frame before checkOwn.
  // `opponentClipTime` (optional): only paint opponent trail up to this game-time -
  // the smooth render clock, i.e. what the player can actually see. Prevents dying
  // against opponent trail your screen hasn't drawn yet. Undefined = paint all
  // received (legacy behaviour).
  function paint(gameState: any, opponentClipTime?: number) {
    if (!bind(gameState)) return;
    const tracks = gameState.wormPathSegments;
    const own = ownTrack(gameState);

    // Own worm: paint finalized segments, leaving the head segment unpainted so the
    // head can collide; track immunity distance. Live segments are STEP-sized (a few
    // px), so one blob at the start forms a continuous trail - but a track adopted
    // from the server (a reconnect resume, a switcharoonie hand-off) holds MERGED
    // segments, and a disconnect gap the server drove can be ONE multi-hundred-unit
    // segment. A single start blob would leave the rest of that trail invisible to
    // local collision (the server still sees it, so we'd sail on locally and die by
    // server verdict, snapping the predicted tail away). Sample long segments along
    // their trajectory, stamping immunity distance progressively like the server.
    function paintOwnSegment(seg: any, trackKey: string, distAtStart: number, flatStamp?: number) {
      const len = seg.speed * seg.duration;
      if (len <= seg.size * 1.5) {
        const cells = gsf.addPlayAreaShape(gameState, { centerX: seg.startX, centerY: seg.startY, radius: seg.size }, trackKey);
        const stamp = flatStamp !== undefined ? flatStamp : distAtStart + len;
        for (let i = 0; i < cells.length; i++) ownDistFromCells[cells[i].index] = stamp;
        return;
      }
      const steps = Math.ceil(len / Math.max(1, seg.size * 0.8));
      for (let k = 0; k <= steps; k++) {
        const p = trajectoryUtil.followTrajectory(seg, (k / steps) * seg.duration);
        const cells = gsf.addPlayAreaShape(gameState, { centerX: p.x, centerY: p.y, radius: seg.size }, trackKey);
        const stamp = flatStamp !== undefined ? flatStamp : distAtStart + (k / steps) * len;
        for (let i = 0; i < cells.length; i++) ownDistFromCells[cells[i].index] = stamp;
      }
    }
    if (own) {
      const segs = tracks[own];
      const limit = segs.length - 1;
      for (; ownCursor < limit; ownCursor++) {
        const seg = segs[ownCursor];
        const distAtStart = ownDist;
        ownDist += seg.speed * seg.duration;
        if (seg.speed > 0 && !seg.jump && seg.type !== "still_arc" && seg.clearedAt === undefined) {
          paintOwnSegment(seg, own, distAtStart);
        }
      }

      // Our own wall-hack ghost tracks ("<own>#N") are the wrapped continuation of OUR
      // trail. Paint them as own trail WITH immunity (stamped at the distance travelled
      // when laid), so we crash into an OLDER wrapped trail exactly like the server does,
      // but stay immune to the fresh wrap we're currently sitting on. Painting each ghost
      // segment once (per-track cursor) means its immunity naturally expires as we move on.
      // (Previously these were skipped entirely, which let the worm sail through its own
      // wrapped trail on the client while the server killed it - causing a death snap.)
      for (const id of Object.keys(tracks)) {
        if (id.indexOf(own + "#") !== 0) continue;
        const gsegs = tracks[id];
        let cur = ghostCursors[id] || 0;
        for (; cur < gsegs.length; cur++) {
          const seg = gsegs[cur];
          if (seg.speed > 0 && !seg.jump && seg.type !== "still_arc" && seg.clearedAt === undefined) {
            // Flat stamp at the current distance (see above): immune now, expiring
            // as we travel on - long ghost segments still get their full geometry.
            paintOwnSegment(seg, id, ownDist, ownDist);
          }
        }
        ghostCursors[id] = cur;
      }
    }

    // Twin spawn immunity: while this worm carries an active "twin" effect it must NOT crash into
    // its twin partner's trail. The two spawn overlapping and have to separate first (much worse
    // when fat: the bigger head reaches the partner's painted trail just behind its head, killing it
    // on the spawn tick). Core gives the twin the original's immunity bookkeeping; here we simply
    // don't paint the partner's trail while the effect is live - advancing the paint cursor so the
    // stretch laid during the overlap is never an obstacle. The server drops the effect once they're
    // apart, then we resume painting the partner from there.
    let ownWormId: any = null;
    if (own && tracks[own] && tracks[own][0]) ownWormId = tracks[own][0].wormId;
    const twinPartners = new Set<any>();
    if (ownWormId != null && gameState.effects) {
      for (const e of gameState.effects) {
        if (e.type === "twin" && e.wormId === ownWormId && e.twinWormId != null) twinPartners.add(e.twinWormId);
      }
    }

    // Opponents: paint by sampling over time (robust to merged/growing segments).
    for (const id of Object.keys(tracks)) {
      const segs = tracks[id];
      // Own main + own ghost tracks are handled above (with immunity); skip them here so
      // they aren't also painted as non-immune opponent trail.
      if (!segs.length || id === own || (own && id.indexOf(own + "#") === 0)) continue;
      // Only up to what the player can see (the render clock), not the freshest
      // received segment - so we don't collide against undrawn opponent trail.
      let latest = segs[segs.length - 1].endTime;
      if (opponentClipTime !== undefined && opponentClipTime < latest) latest = opponentClipTime;
      // Twin partner during spawn immunity: skip its trail (advance the cursor so we never go back
      // and paint the overlap once the effect clears).
      if (twinPartners.has(segs[0].wormId)) { oppPaintedTo[id] = latest; continue; }
      let from = oppPaintedTo[id];
      if (from === undefined) from = segs[0].startTime;
      for (let t = from; t < latest - 1e-9; t += SUB) {
        const data = gsf.getWormPathSegmentDataAtTime(gameState, id, t);
        if (!data) continue;
        const seg = data.segment;
        // (a trail gone from the board is no wall, as soon as we know it went)
        if (seg.speed > 0 && !seg.jump && seg.type !== "still_arc" && seg.clearedAt === undefined) {
          gsf.addPlayAreaShape(gameState, { centerX: data.position.x, centerY: data.position.y, radius: seg.size }, id);
        }
      }
      oppPaintedTo[id] = Math.max(from, latest);
    }
  }

  // Has the local worm's head crashed into a wall, its own trail, or an opponent's?
  function checkOwn(gameState: any): boolean {
    if ((cf as any).getDebugNoCrash && (cf as any).getDebugNoCrash()) return false; // TEMP DEBUG: crashes disabled (?nocrash=1)
    if (gameState && gameState.noCrash) return false; // this match has crashes off (the host's dev-only perf bench)
    if (dead) return true;
    if (!bind(gameState)) return false;
    const id = ownTrack(gameState);
    if (!id) return false;
    const segs = gameState.wormPathSegments[id];
    const head = segs[segs.length - 1];
    if (!head || head.type === "still_arc" || head.speed === 0) return false; // no collision while stationary (start phase or switcharoonie freeze)
    const shape = { centerX: head.endX, centerY: head.endY, radius: head.size };
    if (!shapeSpatialRelations.contains(gameState.map.shape, shape)) {
      // Wall hack: leaving the map is NOT a crash - the worm wraps to the opposite
      // edge (the prediction produces the wrap + ghost trail). Only a real wall (no
      // active wallHack effect on this worm) kills. This runs even mid-jump: a jump /
      // super-jump holes over TRAILS, it is not a wall pass. The trail check below still
      // runs, but a worm outside the bounds paints/collides against out-of-range cells
      // that are guarded as undefined, so it's a no-op until the wrap brings it back in.
      if (!gsf.hasWormEffect(gameState, head.wormId, "wallHack")) {
        dead = true;
        // Death forensics (reload investigation): say exactly what the LOCAL
        // collision decided and from what state, so a mispredicted crash is
        // distinguishable from an honest one in the console.
        crash = {
          cause: "wall",
          head: { x: r1(head.endX), y: r1(head.endY), size: head.size, startTime: r1(head.startTime), endTime: r1(head.endTime), duration: r1(head.duration) },
          ownDist: r1(ownDist),
        };
        console.log("[death:local] wall", JSON.stringify(crash));
        return true;
      }
    }
    if (head.jump) return false; // holing: skip only the trail collision below (wall already checked)
    const pa = gameState.playArea;
    const cells = converter.convert(shape, pa, ShapeToGridConverter.RoundingModes.CONTAINMENT);
    for (let i = 0; i < cells.length; i++) {
      const v = pa.grid[cells[i]];
      if (v !== undefined && v !== PLAY_AREA_FREE) {
        const fromDist = ownDistFromCells[cells[i]];
        const immune = fromDist !== undefined && ownDist - fromDist <= IMMUNITY_DISTANCE_MULTIPLIER * head.size;
        if (!immune) {
          dead = true;
          crash = {
            cause: "trail",
            killer: v,
            cell: { index: cells[i], col: cells[i] % pa.cols, row: Math.floor(cells[i] / pa.cols) },
            stamp: fromDist === undefined ? null : r1(fromDist),
            ownDist: r1(ownDist),
            immunity: IMMUNITY_DISTANCE_MULTIPLIER * head.size,
            // How far each opponent trail was painted into OUR grid when we hit it.
            paintedTo: { ...oppPaintedTo },
            head: { x: r1(head.endX), y: r1(head.endY), size: head.size, startTime: r1(head.startTime), endTime: r1(head.endTime), duration: r1(head.duration) },
          };
          // Is the trail we hit really there? Measured against the killer's track as
          // it stands NOW (the same segments the screen draws), not against our grid.
          try { crash.trace = traceKiller(gameState, String(v), head); } catch { /* forensics only */ }
          console.log("[death:local] trail", JSON.stringify(crash));
          return true;
        }
      }
    }
    return false;
  }
  // "I died in the open" forensics. Our grid said `trackId` has trail under the
  // head. Walk that track and find how close its SOLID trail (moving, not a jump)
  // really comes to the head, and how close any of it does (jumps included).
  // `reach` is the distance at which the two circles touch: a `solidGap` well
  // above 0 means our grid held trail that the track does not (a ghost: painted
  // from a path that later changed, or from another round); a jump right there
  // means we painted, or kept, a hole as solid. `at` is when that trail was laid.
  function traceKiller(gameState: any, trackId: string, head: any) {
    const segs: any[] = (gameState.wormPathSegments && gameState.wormPathSegments[trackId]) || [];
    const own = getOwnTrackId();
    let solid = { d: Infinity, t: 0, size: 0 }, any = { d: Infinity, t: 0, jump: false, still: false };
    // (our own track: only the part behind the head counts, never the head's own segment,
    // and not the stretch just behind it that immunity covers: that is always within
    // reach, so counting it would call every death on our own trail real)
    const isOwn = trackId === own;
    const upTo = isOwn ? segs.length - 1 : segs.length;
    let ownLeft = Infinity; // own track: how far from the head the point being looked at lies
    if (isOwn) { ownLeft = 0; for (let i = 0; i < segs.length; i++) ownLeft += Math.abs(segs[i].speed * segs[i].duration); } // to the head's end
    const immunity = IMMUNITY_DISTANCE_MULTIPLIER * head.size;
    for (let i = 0; i < upTo; i++) {
      const seg = segs[i];
      const len = Math.abs(seg.speed * seg.duration);
      const steps = Math.max(1, Math.ceil(len / 1.5));
      for (let k = 0; k <= steps; k++) {
        if (isOwn && ownLeft - (k / steps) * len <= immunity) break;
        const dt = (k / steps) * seg.duration;
        const p = len > 0 ? trajectoryUtil.followTrajectory(seg, dt) : { x: seg.startX, y: seg.startY };
        const d = Math.hypot(p.x - head.endX, p.y - head.endY);
        const isSolid = seg.speed > 0 && !seg.jump && seg.type !== "still_arc";
        if (d < any.d) any = { d, t: seg.startTime + dt, jump: !!seg.jump, still: !(seg.speed > 0) };
        if (isSolid && d < solid.d) solid = { d, t: seg.startTime + dt, size: seg.size };
      }
      ownLeft -= len;
    }
    const last = segs[segs.length - 1];
    const reach = head.size + (solid.size || head.size);
    return {
      segs: segs.length, lastEnd: last ? r1(last.endTime) : null, own: trackId === own,
      solid: isFinite(solid.d) ? { d: r1(solid.d), at: r1(solid.t), size: solid.size } : null,
      nearest: isFinite(any.d) ? { d: r1(any.d), at: r1(any.t), jump: any.jump, still: any.still } : null,
      reach, solidGap: isFinite(solid.d) ? r1(solid.d - reach) : null,
      ghost: !isFinite(solid.d) || solid.d - reach > 2.5,
    };
  }
  function r1(v: any): any { return typeof v === "number" ? Math.round(v * 1000) / 1000 : v; }

  // A colour left the board (a Respawn round, core respawn.js; the segments are
  // already marked): take its cells off this grid too. Cells not painted yet
  // never will be (paint skips cleared trail).
  function unpaintClear(gameState: any, playerId: any, time: number) {
    if (!grid || !bind(gameState)) return;
    respawn.freeClearedCells(gameState, playerId, time);
  }

  // A new worm takes this grid over (a Respawn round: our crashed worm's), so the
  // opponents' trails painted so far stay and only our own bookkeeping starts over.
  function rebind() {
    ownCursor = 0;
    ownDist = 0;
    ownDistFromCells = {};
    ghostCursors = {};
    dead = false;
    crash = null;
  }

  return { reset, paint, checkOwn, unpaintClear, rebind, isDead: () => dead, getCrash: () => crash };
}
